import React, { useEffect, useState } from 'react';
import { useQueryState, parseAsString } from 'nuqs';
import { useParams, useLocation, useBlocker } from 'react-router-dom';
import { EnumArmProfile, CurrentArmState } from './siteApi';
import { Button } from '@/components/ui/button';
import { TimezoneProvider } from '@/components/TimezoneProvider';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { FeatureFlags } from '@/lib/feature-flag';
import { Spinner } from '@/components/ui/spinner';
import { MapPin, Building2, Camera, Shield, Save, MoreHorizontal } from 'lucide-react';

import { AlertsTable } from '@/features/AdminAlert/components/AlertsTable';
import { SiteLog } from './components/SiteLog';
import { EmbeddedIngestionView } from '@/features/System/ingestion/components/EmbeddedIngestionView';
import { useToast } from '@/hooks/use-toast';
import {
  useGetArmStatus,
  useSite,
  useUpdateNoThreat,
  useUpdateSite,
  useDisarmSite,
} from '@/hooks/siteHooks';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { EnumSirenProfile, EnumAiAutomationLevel } from './siteApi';
import DispositionWorkflowTab from './components/DispositionWorkflowTab';
import { SiteFormValues, siteFormSchema } from './siteSchema';
import SiteSettings from './SiteSettings';
import GenericError from '../ErrorHandling/GenericError';
import { PageTitle } from '@/components/PageTitle';
import FullscreenMap from '@/components/maps/FullscreenMap';
import SiteUsers from './Users/SiteUsers';
import UnifiedDevices from './components/UnifiedDevices';
import { ProjectFullDto } from './siteApi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SiteDangerZone } from './components/SiteDangerZone';
import SiteDevices from './components/SiteDevices';
import { useAuthStore } from '@/store/authStore';
import { UnsavedChangesDialog } from '@/app/Alert/DispositionWorkflow/components/UnsavedChangesDialog';

interface ArmState {
  noThreatDisposition: boolean;
  noThreatMinutes: number;
  selectedArmProfile: EnumArmProfile;
}

interface SiteDetailsProps {
  siteId?: string;
  customerId?: string;
  onSiteUpdate?: (updatedSite: { id: string; name: string }) => void;
}

const SiteDetails: React.FC<SiteDetailsProps> = (props) => {
  // Get parameters either from props or from URL params
  const params = useParams();
  const location = useLocation();
  const siteId = props.siteId || params.siteId;
  const companyId = props.customerId || params.companyId;
  const { toast } = useToast();
  const isSuperAdmin = useAuthStore((s) => s.roles.includes('SuperAdmin'));
  const ingestionEnabled = useFeatureFlagEnabled(FeatureFlags.IngestionViewer);

  const [armState, setArmState] = useState<ArmState>({
    noThreatDisposition: false,
    noThreatMinutes: 0,
    selectedArmProfile: EnumArmProfile.Auto,
  });

  // Tab state synced to URL query parameter
  const [activeTab, setActiveTab] = useQueryState('tab', parseAsString.withDefault('site'));

  const [error, setError] = useState<string | null>(null);
  const [sectionStatuses, setSectionStatuses] = useState<
    Record<string, 'complete' | 'incomplete' | 'pending'>
  >({});

  const { data: site, isLoading: loadingSite } = useSite({
    siteId: siteId || '',
    companyId: companyId || '',
  });

  const {
    data: armStatus,
    refetch: refetchArmStatus,
    isLoading: armStatusLoading,
  } = useGetArmStatus({
    siteId: siteId!,
    companyId: companyId!,
    enabled: !!siteId && !!companyId,
  });

  const { mutateAsync: updateSiteMutation, isPending } = useUpdateSite({
    onSuccess: (data) => {
      toast({
        title: 'Site Updated',
        description: 'Site settings have been updated successfully',
        variant: 'default',
      });
      // Extract the updated site data from the ProjectCrudded response
      if (data.project?.id && data.project?.name) {
        props.onSiteUpdate?.({ id: data.project.id, name: data.project.name });
      }
    },
    onError: () => {
      setError('Failed to update site settings');
    },
  });

  const form = useForm<SiteFormValues>({
    resolver: zodResolver(siteFormSchema),
    defaultValues: {
      customerId: '',
      name: '',
      timeZone: '',
      newMonitoringAlertsAfterSeconds: 0,
      siteNotes: '',
      sirenProfile: EnumSirenProfile.None,
      sirenCooldownSeconds: 0,
      sirenProfilePerson: EnumSirenProfile.None,
      sirenProfileVehicle: EnumSirenProfile.None,
      sirensUnlinked: false,
      aiAutomationLevel: EnumAiAutomationLevel.None,
      aiPrimer: '',
      noThreatAiPrimer: '',
      guardResponseEnabled: false,
      guardResponseDispatchCode: '',
      complianceEnabled: false,
      address: {
        addressLine1: '',
        addressLine2: '',
        city: '',
        region: '',
        postCode: '',
        country: '',
        lat: 0,
        lon: 0,
      },
      // Add the missing properties required by the schema
      externalServiceOptions: {},
      dispositionWorkflowConfig: {},
    },
  });

  const { isDirty } = form.formState;

  // Block navigation when Workflows form has unsaved changes
  const blocker = useBlocker(isDirty);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!site) return;
    form.reset({
      customerId: companyId || '',
      name: site.name || '',
      timeZone: site.timeZone || '',
      newMonitoringAlertsAfterSeconds: site.newMonitoringAlertsAfterSeconds || 0,
      siteNotes: site.siteNotes || '',
      sirenProfile: site.sirenProfile || EnumSirenProfile.None,
      sirenCooldownSeconds: site.sirenCooldownSeconds || 0,
      sirenProfilePerson: site.sirenProfilePerson || EnumSirenProfile.None,
      sirenProfileVehicle: site.sirenProfileVehicle || EnumSirenProfile.None,
      sirensUnlinked: site.sirensUnlinked || false,
      aiAutomationLevel: site.aiAutomationLevel || EnumAiAutomationLevel.None,
      aiPrimer: site.aiPrimer || '',
      noThreatAiPrimer: site.noThreatAiPrimer || '',
      guardResponseEnabled: site.guardResponseEnabled || false,
      guardResponseDispatchCode: site.guardResponseDispatchCode || '',
      complianceEnabled: site.complianceEnabled || false,
      address: {
        addressLine1: site.address?.addressLine1 || '',
        addressLine2: site.address?.addressLine2 || '',
        city: site.address?.city || '',
        region: site.address?.region || '',
        postCode: site.address?.postCode || '',
        country: site.address?.country || '',
        lat: site.address?.lat || 0,
        lon: site.address?.lon || 0,
      },
      // Add the missing properties with site data
      externalServiceOptions: site.externalServiceOptions || {},
      // Use the existing dispositionWorkflowConfig data without transformation
      dispositionWorkflowConfig: site.dispositionWorkflowConfig || {},
    });
  }, [site, form]);

  useEffect(() => {
    if (!site) return;

    setArmState({
      noThreatDisposition:
        site.noThreatDisarmMinutes === null ? false : site.noThreatDisarmMinutes! >= 0,
      noThreatMinutes: site?.noThreatDisarmMinutes || 0,
      selectedArmProfile: site?.armProfile || EnumArmProfile.Auto,
    });
  }, [site]);

  useEffect(() => {
    if (!site) return;

    setSectionStatuses({
      details: site.name && site.timeZone ? 'complete' : 'incomplete',
      contacts: 'complete', // Hardcoded to complete for now
      alerts: site.newMonitoringAlertsAfterSeconds !== undefined ? 'complete' : 'incomplete',
      schedule:
        site.schedule && site.schedule.blocks && site.schedule.blocks.length > 0
          ? 'complete'
          : 'incomplete',
      monitoring:
        site.monitoringCompanies && site.monitoringCompanies.length > 0 ? 'complete' : 'incomplete',
      ai: site.aiAutomationLevel !== EnumAiAutomationLevel.None ? 'complete' : 'incomplete',
      sirens: site.sirenProfile !== EnumSirenProfile.None ? 'complete' : 'incomplete',
      guards: site.guardResponseEnabled ? 'complete' : 'incomplete',
    });
  }, [site]);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const onSubmit = async (data: SiteFormValues) => {
    if (!siteId || !companyId) return;
    try {
      await updateSiteMutation({
        customerId: companyId,
        projectId: siteId,
        data,
      });
    } catch (error) {
      console.error('Failed to update site:', error);
    }
  };

  if (loadingSite || armStatusLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <GenericError
        title="Error loading site"
        description="The requested site could not be loaded."
        statusCode={500}
        statusText="The site could not be loaded :("
      />
    );
  }

  if (!site) {
    return (
      <GenericError
        title="Site Not Found"
        description="The requested site could not be found."
        statusCode={404}
        statusText="The site could not be found :("
      />
    );
  }

  return (
    <TimezoneProvider timeZone={site.timeZone} label="Site timezone" entityName={site.name}>
      <PageTitle title={site.name || 'Site Details'} />
      <UnsavedChangesDialog
        open={blocker.state === 'blocked'}
        onClose={() => blocker.reset?.()}
        onConfirm={() => blocker.proceed?.()}
      />
      <div
        data-onboarding="site-detail-header"
        className={`sticky z-[20] flex items-center gap-4 border-b bg-[#111] p-2 ${
          location.pathname.includes('/viewer') ? 'top-0' : 'top-[56px]'
        }`}
      >
        <div className="flex items-center gap-2">
          <Building2 className="h-6 w-6" />
          <div>
            <h1 className="text-lg font-semibold text-white">{site?.name}</h1>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div className="flex items-center gap-1 rounded-full bg-gray-800 px-3 py-1 text-xs font-semibold text-white">
            <Camera className="h-4 w-4 text-gray-400" />
            <span>{new Set(site?.availableVideoProcessors?.map(vp => vp.assetId)?.filter(Boolean)).size || 0} Devices</span>
          </div>
          <div className="flex items-center gap-1 rounded-full bg-gray-800 px-3 py-1 text-xs font-semibold text-white">
            <MapPin className="h-4 w-4 text-gray-400" />
            <span>{site?.monitoringZones?.length || 0} Zones</span>
          </div>
          <div
            className={`flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold text-white ${
              armStatus?.status === CurrentArmState.Armed
                ? 'bg-green-800/30'
                : armStatus?.status === CurrentArmState.TestMode
                  ? 'bg-orange-800/30'
                  : 'bg-red-800/30'
            }`}
          >
            <Shield
              className={`h-4 w-4 ${
                armStatus?.status === CurrentArmState.Armed
                  ? 'text-green-400'
                  : armStatus?.status === CurrentArmState.TestMode
                    ? 'text-orange-400'
                    : 'text-red-400'
              }`}
            />
            <span>
              {armStatus?.status === CurrentArmState.Armed
                ? 'Armed'
                : armStatus?.status === CurrentArmState.TestMode
                  ? 'Test Mode'
                  : 'Disarmed'}
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-onboarding="site-detail-actions"
                variant="outline"
                size="sm"
                className=""
              >
                <MoreHorizontal className="mr-1 h-4 w-4" />
                Actions
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-lime z-[100] w-48 font-semibold">
              <SiteDangerZone
                customerId={companyId!}
                siteId={siteId!}
                site={site as ProjectFullDto}
                archived={Boolean(site?.archivedAt)}
                asDropdownItem
              />
            </DropdownMenuContent>
          </DropdownMenu>{' '}
          <Button
            type="submit"
            form="site-form"
            disabled={isPending}
            className="bg-lime hover:bg-lime/90 text-black"
            data-onboarding="site-detail-save"
          >
            <Save className="h-4 w-4" />
            {isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
      <Tabs
        defaultValue="site"
        value={activeTab}
        onValueChange={handleTabChange}
        className="w-full"
      >
        <TabsList
          data-onboarding="site-detail-tabs"
          className="sticky top-14 z-50 w-full min-w-fit rounded-none border-x-0 bg-[#111]"
        >
          <TabsTrigger value="site" className="flex-1">
            Site Details
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex-1">
            Alerts
          </TabsTrigger>

          <TabsTrigger value="components" className="flex-1">
            Components
          </TabsTrigger>

          <TabsTrigger value="devices" className="flex-1">
            Devices
          </TabsTrigger>

          <TabsTrigger value="workflows" className="flex-1">
            Workflows
          </TabsTrigger>

          <TabsTrigger value="users" className="flex-1">
            Site Users
          </TabsTrigger>

          <TabsTrigger value="logs" className="flex-1">
            {' '}
            Logs
          </TabsTrigger>

          {isSuperAdmin && ingestionEnabled && (
            <TabsTrigger value="ingestion" className="flex-1">
              Ingestion
            </TabsTrigger>
          )}
        </TabsList>
        <div data-onboarding="site-detail-content" className="mx-auto w-full max-w-full">
          <TabsContent value="site">
            <SiteSettings
              siteId={siteId!}
              customerId={companyId!}
              onSiteUpdate={props.onSiteUpdate}
            />
          </TabsContent>

          <TabsContent value="alerts" className="">
            <AlertsTable admin siteId={siteId!} showProjectFilter={false} showPageHeader={false} />
          </TabsContent>

          <TabsContent value="components">
            <div className="rounded-lg border bg-[#111] text-white">
              <UnifiedDevices siteId={siteId!} companyId={companyId!} />
            </div>
          </TabsContent>

          <TabsContent value="devices">
            <SiteDevices siteId={siteId!} companyId={companyId!} />
          </TabsContent>

          <TabsContent value="workflows">
            <div className="p-6">
              <h2 className="mb-4 text-xl font-semibold text-white">Disposition Workflows</h2>
              <DispositionWorkflowTab siteData={site} form={form} onSubmit={onSubmit} />
            </div>
          </TabsContent>

          <TabsContent value="logs">
            <SiteLog projectId={siteId!} />
          </TabsContent>

          <TabsContent value="users">
            <SiteUsers siteId={siteId!} companyId={companyId!} />
          </TabsContent>

          {isSuperAdmin && ingestionEnabled && (
            <TabsContent value="ingestion">
              <EmbeddedIngestionView lockedFilters={{ projectId: siteId }} />
            </TabsContent>
          )}
        </div>
      </Tabs>
    </TimezoneProvider>
  );
};

export default SiteDetails;
